import { create, meta } from '@dicebear/avataaars'
import { createAvatar } from '@dicebear/core'
import React, { useMemo } from 'react'

import {
  type LegacyAvataaarsProps,
  buildDiceBearOptions
} from './dicebear-mapper'

interface AvataaarsRendererProps extends LegacyAvataaarsProps {
  style?: React.CSSProperties
}

/**
 * Drop-in replacement for the unmaintained `avataaars` package.
 * Accepts the same props as the old <Avataaars> component and renders
 * equivalent SVG via @dicebear/avataaars.
 *
 * Accepts the `avatarStyle` prop: 'Circle' renders with a circle background;
 * 'Transparent' renders with no background (DiceBear 'default' style).
 * The SVG is injected inline so it fills its container exactly as the
 * original component did with style={{ width: '100%', height: '100%' }}.
 * DiceBear's SVG content is deterministic and trusted — no user-supplied
 * HTML is ever set via dangerouslySetInnerHTML.
 */
function AvataaarsRenderer({
  style,
  avatarStyle,
  ...props
}: AvataaarsRendererProps) {
  const svgString = useMemo(() => {
    const options = buildDiceBearOptions({ ...props, avatarStyle })
    const raw = createAvatar(
      { meta, create },
      options as Parameters<typeof createAvatar>[1]
    ).toString()
    // Ensure the SVG scales to fill its container, matching the old
    // component's style={{ width: '100%', height: '100%' }} behavior.
    return raw
      .replace(/(<svg\b[^>]*)\bwidth="[^"]*"/, '$1width="100%"')
      .replace(/(<svg\b[^>]*)\bheight="[^"]*"/, '$1height="100%"')
  }, [
    props.topType,
    props.hairColor,
    props.accessoriesType,
    props.facialHairType,
    props.clotheType,
    props.clotheColor,
    props.eyeType,
    props.eyebrowType,
    props.mouthType,
    props.skinColor
  ])

  return (
    <span
      style={{ display: 'inline-block', lineHeight: 0, ...style }}
      dangerouslySetInnerHTML={{ __html: svgString }}
    />
  )
}

export default AvataaarsRenderer
